/**
 * Apollo Client Configuration for Backoffice
 * @implements US-099 - Backoffice Dashboard View
 * @implements FS-003 - GraphQL API Architecture
 *
 * HTTP-only Apollo Client (no WebSocket — dashboard is pull-based).
 */

import { ApolloClient, InMemoryCache, ApolloLink, HttpLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';

/**
 * Get the authentication token from localStorage
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

/**
 * HTTP Link for queries and mutations
 * Uses the Vite proxy in development (/graphql -> localhost:3201/graphql)
 */
const httpLink = new HttpLink({
  uri: '/graphql',
  credentials: 'include',
});

/**
 * Authentication link to add Bearer token to requests
 * @implements FS-003.5.1 - Token format is `Bearer <token>`
 */
const authLink = new ApolloLink((operation, forward) => {
  const token = getAuthToken();

  operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }));

  return forward(operation);
});

/**
 * Error link for global error handling
 */
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions, path }) => {
      const code = extensions?.code as string | undefined;

      if (code === 'UNAUTHENTICATED') {
        console.warn('[Apollo] Session expired or invalid');
      } else {
        console.error(
          `[GraphQL error]: Message: ${message}, Code: ${
            code || 'UNKNOWN'
          }, Path: ${path?.join('.')}`,
        );
      }
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError.message}`);
  }
});

/**
 * Apollo Client instance for Backoffice
 * HTTP-only (no subscriptions needed for dashboard)
 */
export const apolloClient = new ApolloClient({
  link: errorLink.concat(authLink.concat(httpLink)),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  },
  connectToDevTools: import.meta.env.DEV,
});
